<template>
  <page-wrapper :title="title">
    <!-- Loading Section -->
    <LoadingOverlay :active="loading" />

    <div class="page-breadcrumbs">
      <nav class="custom-breadcrumbs">
        <a class="breadcrumb-link" @click="$router.back()">หน้าหลัก Virtual Sport</a>
        <span class="separator">›</span>
        <span class="current-page">รายละเอียดการประเมิน</span>
      </nav>
    </div>

    <div class="virtual-sport-detail">
      <!-- Detail Content -->
      <div class="detail-container">
        <div class="image-section">
          <div class="image-placeholder">
            <img
              v-if="details.evidenceImageUrl"
              :src="details.evidenceImageUrl"
              class="evidence-image"
            />
            <div v-else class="no-image">
              <i class="el-icon-picture-outline"></i>
              <p>รูปภาพ</p>
            </div>
          </div>
        </div>

        <div class="info-section">
          <div class="header-info">
            <p class="address-info">{{ details.projectCode }}</p>
            <p class="address-info">{{ details.addressNo }}</p>
            <p class="title">{{ details.customerName }}</p>
            <p
              v-if="
                oldStatus === approvalStatuses.APPROVED ||
                oldStatus === approvalStatuses.REJECTED
              "
              :class="[
                'approval-status',
                oldStatus === approvalStatuses.APPROVED
                  ? 'approved'
                  : 'rejected',
              ]"
            >
              {{
                oldStatus === approvalStatuses.APPROVED
                  ? "รายการอนุมัติ"
                  : "รายการไม่อนุมัติ"
              }}
            </p>
          </div>

          <div class="status-section">
            <div>
              <input
                v-model="selectedStatus"
                :value="approvalStatuses.APPROVED"
                type="radio"
                name="approval"
              />
              <span>อนุมัติ</span>
            </div>
            <div>
              <input
                v-model="selectedStatus"
                :value="approvalStatuses.REJECTED"
                type="radio"
                name="approval"
              />
              <span>ไม่อนุมัติ</span>
            </div>
          </div>

          <div class="form-section">
            <!-- Info -->
            <div v-if="!isRejected" class="form-inputs">
              <div>
                <label class="form-label">แคลอรี่</label>
                <el-input
                  v-model.number="calories"
                  type="number"
                  placeholder="แคลอรี่"
                />
              </div>

              <div>
                <label class="form-label">เวลาที่ออกกำลังกาย (นาที)</label>
                <el-input
                  v-model.number="details.workoutDurationMinutes"
                  type="number"
                  placeholder="นาที"
                />
              </div>
            </div>

            <!-- Rejection Reason -->
            <div v-if="isRejected" class="form-inputs">
              <div class="form-group">
                <label class="form-label">เหตุผล</label>
                <el-select v-model="details.rejectionReason" placeholder="เหตุผล">
                  <el-option
                    v-for="option in rejectionReasonOptions"
                    :key="option.value"
                    :label="option.label"
                    :value="option.value"
                  />
                </el-select>
              </div>

              <div>
                <label>หมายเหตุ (ไม่แสดงให้ลูกบ้านเห็น) </label>
                <el-input
                  v-model="details.adminRemark"
                  type="text"
                  placeholder="ระบุหมายเหตุเพิ่มเติม"
                />
              </div>
            </div>
          </div>

          <!-- Score Display -->
          <div v-if="!isRejected" class="score-section">
            <div class="score-display">
              <span class="score-number">{{ details.point }}</span>
              <img src="/static/icons/star.svg" alt="star" width="24" />
            </div>
          </div>

          <!-- Action Buttons -->
          <div class="action-buttons">
            <sc-button :disabled="submitting" variant="outline" @click="handleCancel"
              >ยกเลิก</sc-button
            >
            <sc-button :disabled="submitting" variant="primary" @click="handleSubmit"
              >บันทึกรายการ</sc-button
            >
          </div>
        </div>
      </div>

      <!-- Activity Log Section -->
      <div
        v-if="details.adminActivityLogs && details.adminActivityLogs.length > 0"
        class="activity-log-section"
      >
        <h3 class="activity-log-title">Activity Log</h3>
        <div class="activity-log-container">
          <div
            v-for="(log, index) in details.adminActivityLogs"
            :key="index"
            class="activity-log-item"
          >
            {{ log }}
          </div>
        </div>
      </div>
    </div>
  </page-wrapper>
</template>

<script>
import PageWrapper from "@/lib/components/PageWrapper";
import ScButton from "@/lib/components/ScButton";
import { backendAdminClient } from "@/dependencies";
import LoadingOverlay from "@/lib/components/LoadingOverlay";
import {
  virtualSportApprovalStatus,
  virtualSportRejectionReason,
} from "../config/enums";

export default {
  components: {
    PageWrapper,
    ScButton,
    LoadingOverlay,
  },
  data() {
    return {
      title: "รายละเอียดการประเมิน",
      calories: undefined,
      caloriesPerPoint: 10,
      selectedStatus: virtualSportApprovalStatus.APPROVED,
      oldStatus: undefined,
      loading: false,
      submitting: false,
      projectId: this.$route.params.id,
      details: {
        id: undefined,
        evidenceImageUrl: "",
        projectCode: "",
        addressNo: "",
        customerName: "",
        workoutDurationMinutes: undefined,
        rejectionReason: "",
        adminRemark: "",
        point: 0,
        adminActivityLogs: [],
      },
    };
  },
  computed: {
    approvalStatuses() {
      return virtualSportApprovalStatus;
    },
    isRejected() {
      return this.selectedStatus === virtualSportApprovalStatus.REJECTED;
    },
    rejectionReasonOptions() {
      return Object.entries(virtualSportRejectionReason).map(
        ([key, label]) => ({
          label,
          value: key,
        })
      );
    },
  },
  watch: {
    calories(value) {
      this.details.point = value ? Math.floor(value / this.caloriesPerPoint) : 0;
    },
  },
  created() {
    this.setupData();
  },
  methods: {
    handleCancel() {
      this.$router.back();
    },

    getRejectionReasonLabel(reason) {
      return virtualSportRejectionReason[reason] || reason;
    },

    handleSubmit() {
      if (!this.validateSubmit()) return;

      const isRejectedSubmit = this.isRejected;
      const minutes = Number(this.details.workoutDurationMinutes);

      const formData = {
        status: this.selectedStatus,
        calories: this.calories ? Number(this.calories) : 0,
        workout_duration_seconds: minutes ? minutes * 60 : undefined,
        rejection_reason: isRejectedSubmit ? this.details.rejectionReason : undefined,
        admin_remark: isRejectedSubmit ? this.details.adminRemark : undefined,
      };

      this.patchData(this.details.id, formData);
    },

    validateSubmit() {
      if (this.selectedStatus === this.approvalStatuses.APPROVED) {
        if (!this.calories || Number(this.calories) <= 0) {
          this.$message.error("กรุณาระบุค่าแคลอรี่");
          return false;
        }
        if (
          !this.details.workoutDurationMinutes ||
          Number(this.details.workoutDurationMinutes) <= 0
        ) {
          this.$message.error("กรุณาระบุเวลาที่ออกกำลังกาย");
          return false;
        }
      } else if (this.isRejected) {
        if (!this.details.rejectionReason) {
          this.$message.error("กรุณาเลือกเหตุผลการไม่อนุมัติ");
          return false;
        }
        if (
          this.details.rejectionReason === "OTHER" &&
          (!this.details.adminRemark || this.details.adminRemark.trim() === "")
        ) {
          this.$message.error("กรุณาระบุหมายเหตุเมื่อเลือกเหตุผล 'อื่นๆ'");
          return false;
        }
      }
      return true;
    },

    mapResponseToDetails(result) {
      return {
        id: this.projectId,
        projectCode: result.project_code,
        addressNo: result.address_no,
        customerName: result.customer_name,
        evidenceImageUrl: result.evidence_image_url,
        workoutDurationMinutes: result.workout_duration_seconds
          ? Math.floor(result.workout_duration_seconds / 60)
          : undefined,
        rejectionReason: result.rejection_reason,
        adminRemark: result.admin_remark,
        point: result.point || 0,
        adminActivityLogs: result.admin_activity_logs || [],
      };
    },

    async setupData() {
      const result = await this.fetchData();

      this.details = this.mapResponseToDetails(result);
      this.calories = result.calories;
      this.caloriesPerPoint = result.calories_per_point || 10;
      this.oldStatus = result.status;
      this.selectedStatus =
        result.status === this.approvalStatuses.PENDING
          ? this.approvalStatuses.APPROVED
          : result.status;
    },

    async fetchData() {
      try {
        this.loading = true;
        const { data } = await backendAdminClient.virtualSport.getDetail(this.projectId);
        return data.result;
      } catch (error) {
        this.$message.error(
          "เกิดข้อผิดพลาดในการโหลดข้อมูล กรุณาลองใหม่อีกครั้ง"
        );
        return {};
      } finally {
        this.loading = false;
      }
    },

    async patchData(id, updatedParam) {
      this.submitting = true;
      try {
        await backendAdminClient.virtualSport.patch(id, updatedParam);

        await this.$alert("บันทึกข้อมูลเรียบร้อยแล้ว", "สำเร็จ", {
          confirmButtonText: "ตกลง",
          type: "success",
          showClose: false,
        });

        this.$router.back();
      } catch (error) {
        await this.$alert(
          "เกิดข้อผิดพลาดในการบันทึกข้อมูล กรุณาลองใหม่อีกครั้ง",
          "แจ้งเตือน",
          {
            confirmButtonText: "ปิด",
            type: "warning",
          }
        );
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.page-breadcrumbs {
  margin-top: 16px;
  margin-left: 24px;
}

.custom-breadcrumbs {
  display: flex;
  align-items: center;
  gap: 8px;

  .breadcrumb-link {
    color: #666;
    text-decoration: none;
    cursor: pointer;

    &:hover {
      color: #333;
      text-decoration: underline;
    }
  }

  .separator {
    color: #999;
    font-size: 16px;
    margin: 0 4px;
  }

  .current-page {
    color: #333;
    font-weight: 600;
  }
}

.virtual-sport-detail {
  padding: 0 24px;
  margin-bottom: 24px;
}

.detail-container {
  background: white;
  border-radius: 16px;
  padding: 32px;
  display: flex;
  gap: 40px;
  max-width: 1200px;
}

.image-section {
  flex: 0 0 300px;
}

.image-placeholder {
  width: 100%;
  height: 400px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  .evidence-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 10px;
  }

  .no-image {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    i {
      font-size: 48px;
      margin-bottom: 8px;
    }

    p {
      font-size: 16px;
      margin: 0;
    }
  }
}

.info-section {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.header-info {
  display: flex;
  align-items: baseline;

  .address-info {
    color: $orange;
    font-size: 24px;
    font-weight: bold;
    padding-right: 4px;
  }

  .title {
    padding-left: 4px;
    font-size: 24px;
    font-weight: 700;
    margin: 0;
  }

  .approval-status {
    margin-left: 20px;
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;

    &.approved {
      background-color: #f0f9ff;
      color: #10b981;
      border: 1px solid #10b981;
    }

    &.rejected {
      background-color: #fef2f2;
      color: #ef4444;
      border: 1px solid #ef4444;
    }
  }
}

.status-section {
  display: flex;
  gap: 24px;
  margin: 12px 0;
  font-size: large;
}

.form-section {
  display: flex;
  gap: 20px;
}

.form-inputs {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  margin: 0;
}

.form-label {
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 8px;
}

.score-section {
  .score-display {
    padding-top: 16px;
    display: flex;
    align-items: center;
    gap: 8px;

    .score-number {
      font-size: 36px;
      font-weight: bold;
      padding-right: 4px;
    }
  }
}

.activity-log-section {
  padding: 0 32px;

  .activity-log-title {
    font-size: 18px;
    font-weight: 600;
    margin-bottom: 16px;
  }

  .activity-log-item {
    font-size: 14px;
    color: #666;
    line-height: 1.5;
    padding: 4px 0;
  }
}

.action-buttons {
  display: flex;
  gap: 16px;
  padding-top: 32px;
}

// Responsive Design
@media (max-width: 1024px) {
  .detail-container {
    flex-direction: column;
    gap: 24px;
    padding: 24px;
  }

  .image-section {
    flex: none;

    .image-placeholder {
      height: 250px;
    }
  }
}

@media (max-width: 768px) {
  .virtual-sport-detail {
    padding: 0 16px;
  }

  .detail-container {
    padding: 20px;
    border-radius: 12px;
  }

  .header-info {
    .title {
      font-size: 20px;
    }
  }

  .action-buttons {
    flex-direction: column-reverse;

    .btn {
      width: 100%;
      padding: 14px;
    }
  }
}

@media (max-width: 480px) {
  .detail-container {
    padding: 16px;
  }

  .image-placeholder {
    height: 200px;

    i {
      font-size: 36px;
    }

    p {
      font-size: 14px;
    }
  }

  .header-info {
    .code-badge {
      font-size: 12px;
      padding: 6px 12px;
    }

    .title {
      font-size: 18px;
    }
  }

  .score-display {
    .score-number {
      font-size: 28px;
    }

    .score-star {
      font-size: 24px;
    }
  }
}
</style>
